/* ==========================================================================
   Kopstorm CRO Suite - kopstorm-cro.js
   Behavior for the flag-gated CRO components. Loaded deferred, only when
   the suite is enabled in theme settings. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var money = function (cents, symbol) {
    var value = (cents / 100).toFixed(2).replace('.', ',');
    return (symbol || '') + value;
  };

  /* ------------------------------------------------------------------------
     T16 - Gift mode
     Reveals the note field and attaches the note as a line item property on
     the product form, so it flows through checkout and order confirmation.
     ------------------------------------------------------------------------ */
  if (!customElements.get('kopstorm-gift-mode')) {
    customElements.define('kopstorm-gift-mode', class extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-gift-toggle]');
        this.panel = this.querySelector('[data-gift-panel]');
        this.note = this.querySelector('[data-gift-note]');
        if (!this.toggle || !this.panel) return;

        this.toggle.addEventListener('change', this.sync.bind(this));
        if (this.note) this.note.addEventListener('input', this.sync.bind(this));
      }
      sync() {
        var open = this.toggle.checked;
        this.panel.hidden = !open;

        var form = document.getElementById(this.dataset.formId);
        if (!form) return;

        var giftNote = form.querySelector('input[name="properties[Gift note]"]');

        if (open) {
          if (!giftNote) {
            giftNote = document.createElement('input');
            giftNote.type = 'hidden';
            giftNote.name = 'properties[Gift note]';
            form.appendChild(giftNote);
          }
          giftNote.value = this.note ? this.note.value.trim() : '';
          if (!giftNote.value) giftNote.disabled = true;
          else giftNote.disabled = false;
        } else {
          if (giftNote) giftNote.remove();
        }
      }
    });
  }

  /* ------------------------------------------------------------------------
     T20 - Free shipping progress nudge on the PDP
     Renders at load from Liquid data, then refreshes after any add to cart.
     ------------------------------------------------------------------------ */
  if (!customElements.get('kopstorm-shipping-nudge')) {
    customElements.define('kopstorm-shipping-nudge', class extends HTMLElement {
      connectedCallback() {
        this.threshold = parseInt(this.dataset.threshold, 10) || 0;
        this.total = parseInt(this.dataset.cartTotal, 10) || 0;
        this.text = this.querySelector('[data-nudge-text]');
        this.fill = this.querySelector('[data-nudge-fill]');
        if (!this.threshold || !this.text) return;

        this.render();

        this.refresh = this.refresh.bind(this);
        document.addEventListener('cart:updated', this.refresh);
        document.addEventListener('cart:refresh', this.refresh);
        document.addEventListener('submit', function (event) {
          if (event.target && event.target.getAttribute('data-type') === 'add-to-cart-form') {
            setTimeout(this.refresh, 1600);
          }
        }.bind(this), true);
      }
      refresh() {
        var self = this;
        fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) { return response.json(); })
          .then(function (cart) {
            self.total = cart.total_price;
            self.render();
          })
          .catch(function () { /* leave last known state */ });
      }
      render() {
        var remaining = this.threshold - this.total;
        var symbol = this.dataset.moneyFormat || '';

        if (remaining <= 0) {
          this.text.innerHTML = '<strong>' + this.text.dataset.msgUnlocked + '</strong>';
        } else {
          var price = parseInt(this.dataset.price, 10) || 0;
          var message = this.text.dataset.msgRemaining.replace('[amount]', '<strong>' + money(remaining, symbol) + '</strong>');
          if (price >= remaining && this.total === 0) {
            message = this.text.dataset.msgThisUnlocks;
          }
          this.text.innerHTML = message;
        }
        if (this.fill) {
          var progress = this.threshold > 0 ? Math.min(100, Math.round((this.total / this.threshold) * 100)) : 0;
          this.fill.style.setProperty('--kop-progress', progress + '%');
        }
      }
    });
  }

  /* ------------------------------------------------------------------------
     T28 - Reservation module (timer mode)
     Countdown persists per session so reopening the drawer does not reset it.
     On expiry it settles into calm reassurance instead of punishing the
     shopper with 00:00.
     ------------------------------------------------------------------------ */
  if (!customElements.get('kopstorm-reservation')) {
    customElements.define('kopstorm-reservation', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.mode !== 'timer') return;
        this.clock = this.querySelector('[data-reserve-clock]');
        if (!this.clock) return;

        var minutes = parseInt(this.dataset.minutes, 10) || 10;
        var key = 'kopstorm:reserve-until';
        var until = 0;
        try { until = parseInt(sessionStorage.getItem(key), 10) || 0; } catch (e) { /* storage unavailable */ }
        if (!until || until < Date.now()) {
          until = Date.now() + minutes * 60000;
          try { sessionStorage.setItem(key, String(until)); } catch (e) { /* storage unavailable */ }
        }
        this.until = until;
        this.tick();
        this.interval = setInterval(this.tick.bind(this), 1000);
      }
      disconnectedCallback() {
        if (this.interval) clearInterval(this.interval);
      }
      tick() {
        var left = Math.max(0, this.until - Date.now());
        var mm = String(Math.floor(left / 60000)).padStart(2, '0');
        var ss = String(Math.floor((left % 60000) / 1000)).padStart(2, '0');
        this.clock.textContent = mm + ':' + ss;
        if (left <= 0) {
          clearInterval(this.interval);
          var calm = document.querySelector('kopstorm-reservation[data-mode="calm"] .kop-reserve__text');
          if (calm) {
            this.querySelector('.kop-reserve__text').textContent = calm.textContent;
            this.classList.remove('kop-reserve--timer');
          }
        }
      }
    });
  }

  /* ------------------------------------------------------------------------
     T1 - Geo suggestion banner
     Compares the browser locale region with the active market. Zero external
     lookups: the region hint ships with every request in navigator.languages.
     ------------------------------------------------------------------------ */
  if (!customElements.get('kopstorm-geo-banner')) {
    customElements.define('kopstorm-geo-banner', class extends HTMLElement {
      connectedCallback() {
        var DISMISS_KEY = 'kopstorm:geo-dismissed';
        try { if (sessionStorage.getItem(DISMISS_KEY)) return; } catch (e) { /* storage unavailable */ }

        var current = (this.dataset.currentCountry || '').toUpperCase();
        var dataEl = this.querySelector('[data-geo-countries]');
        if (!dataEl) return;

        var countries;
        try { countries = JSON.parse(dataEl.textContent); } catch (e) { return; }

        var self = this;
        fetch('/browsing_context_suggestions.json?country%5Benabled%5D=true')
          .then(function (response) { return response.ok ? response.json() : null; })
          .then(function (data) {
            var detectedCountry = data && data.detected_values && data.detected_values.country;
            var detected = detectedCountry && (detectedCountry.handle || detectedCountry.iso_code);
            if (!detected) return;
            detected = String(detected).toUpperCase();
            if (detected === current) return;

            var suggestion = countries.find(function (country) { return country.iso === detected; });
            if (!suggestion) return;

            var text = self.querySelector('[data-geo-text]');
            text.innerHTML = text.dataset.msg
              .replace('[country]', '<strong>' + suggestion.name + '</strong>')
              .replace('[currency]', suggestion.currency);

            var pin = self.querySelector('.kop-geo__pin');
            if (pin && suggestion.flag) {
              var pinFallback = pin.innerHTML;
              var flagImg = document.createElement('img');
              flagImg.className = 'kop-geo__flag';
              flagImg.src = suggestion.flag;
              flagImg.alt = '';
              flagImg.width = 20;
              flagImg.height = 20;
              flagImg.loading = 'lazy';
              flagImg.onerror = function () {
                /* No flag asset for this country: keep the neutral pin icon */
                pin.innerHTML = pinFallback;
              };
              pin.innerHTML = '';
              pin.appendChild(flagImg);
            }

            self.querySelector('[data-geo-country-input]').value = suggestion.iso;
            self.hidden = false;
          })
          .catch(function () { /* stay hidden on any failure */ });

        this.querySelector('.kop-geo__form').addEventListener('submit', function () {
          try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* storage unavailable */ }
        });

        this.querySelector('[data-geo-dismiss]').addEventListener('click', function () {
          try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* storage unavailable */ }
          this.hidden = true;
        }.bind(this));
      }
    });
  }

  /* ------------------------------------------------------------------------
     T14 - Skill badge popover
     The badge is a button; tapping it opens a small card that explains the
     three difficulty tiers. Closes on outside click, Escape, or re-tap.
     ------------------------------------------------------------------------ */
  if (!customElements.get('kopstorm-skill-badge')) {
    customElements.define('kopstorm-skill-badge', class extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-skill-toggle]');
        this.pop = this.querySelector('[data-skill-pop]');
        if (!this.toggle || !this.pop) return;
        this.onDocClick = this.onDocClick.bind(this);
        this.onKey = this.onKey.bind(this);
        this.toggle.addEventListener('click', function (event) {
          event.preventDefault();
          this.setOpen(this.pop.hidden);
        }.bind(this));
      }
      setOpen(open) {
        this.pop.hidden = !open;
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          document.addEventListener('click', this.onDocClick);
          document.addEventListener('keydown', this.onKey);
        } else {
          document.removeEventListener('click', this.onDocClick);
          document.removeEventListener('keydown', this.onKey);
        }
      }
      onDocClick(event) {
        if (!this.contains(event.target)) this.setOpen(false);
      }
      onKey(event) {
        if (event.key === 'Escape') {
          this.setOpen(false);
          this.toggle.focus();
        }
      }
      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
      }
    });
  }

  /* ------------------------------------------------------------------------
     T2 - Localized selector attributes
     Visible strings swap via CSS; placeholder and aria-label attributes
     cannot, so they are synced here whenever the flag is (or becomes) active.
     ------------------------------------------------------------------------ */
  var syncT02 = function () {
    if (!document.documentElement.classList.contains('kop-t02')) return;
    document.querySelectorAll('[data-kop-t02-placeholder]').forEach(function (el) {
      el.placeholder = el.dataset.kopT02Placeholder;
      if (el.dataset.kopT02Aria) el.setAttribute('aria-label', el.dataset.kopT02Aria);
    });
  };
  syncT02();
  document.addEventListener('kopstorm:flag', function (event) {
    if (event.detail && event.detail.id === 't02') syncT02();
  });

  /* ------------------------------------------------------------------------
     T47 - Bottom navigation
     Opens the theme drawers where available (falls back to plain links) and
     yields to the native sticky ATC bar on product pages.
     ------------------------------------------------------------------------ */
  if (!customElements.get('kopstorm-bottom-nav')) {
    customElements.define('kopstorm-bottom-nav', class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', function (event) {
          var control = event.target.closest('[aria-controls]');
          if (!control) return;
          var drawer = document.getElementById(control.getAttribute('aria-controls'));
          if (!drawer) return;
          event.preventDefault();
          if (typeof drawer.show === 'function') drawer.show();
          else {
            drawer.hidden = false;
            drawer.dispatchEvent(new CustomEvent('open', { bubbles: true }));
            control.setAttribute('aria-expanded', 'true');
          }
        });

      }
    });
  }
})();

/* T2 - swap attribute strings (placeholder, aria-label) when the flag is on */
(function () {
  'use strict';
  var apply = function () {
    if (!document.documentElement.classList.contains('kop-t02')) return;
    document.querySelectorAll('[data-kop-t02-placeholder]').forEach(function (el) {
      el.placeholder = el.dataset.kopT02Placeholder;
      if (el.dataset.kopT02Aria) el.setAttribute('aria-label', el.dataset.kopT02Aria);
    });
  };
  document.addEventListener('kopstorm:flag', function (event) {
    if (event.detail.id === 't02') apply();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
  else apply();
})();
